import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { Location } from '@angular/common';
import { ActivatedRoute, NavigationEnd, Router, RouterModule } from '@angular/router';
import { filter } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { RegimenClass } from '@cureskin/api-client/src/server-api';
import { ConnectionService } from '@services/connection-service';
import { RegimenService } from '@services/regimen-service/regimen.service';
import { InstructionsService } from '@services/instructions-service/instructions.service';
import { AppWebBridgeService } from '@services/app-web-bridge-service';
import { CurrentComponentService } from '@services/current-component';
import { AppConfig } from 'client/app/app.config';
import { ToolbarTabsModule } from '@components/toolbar-tabs';
import { CsIconModule } from '@components/icon/icon.module';
import { LoadingModule } from '@components/loading';
import { ProcessedRegimen, RegimenTabClass, RegimenViewState, TabChangeEvent, TabData } from '../regimen.types';
import { processRegimen, getDisplayState } from '../regimen.utils';
import {
  REGIMEN_ROUTE_PATHS, REGIMEN_TAB_TYPES, REGIMEN_QUERY_PARAMS, REGIMEN_STATUS_BAR_COLOR, REGIMEN_VIEW_STATES,
} from '../regimen.constants';

@Component({
  selector: 'regimen-view-shell',
  standalone: true,
  imports: [RouterModule, ToolbarTabsModule, CsIconModule, LoadingModule],
  templateUrl: './regimen-view-shell.component.html',
  styles: ':host { @apply tw-block tw-h-full; }',
})
export class RegimenShellComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly location = inject(Location);
  private readonly conn = inject(ConnectionService);
  private readonly regimenService = inject(RegimenService);
  private readonly appConfig = inject(AppConfig);
  private readonly instructionsService = inject(InstructionsService);
  private readonly appBridge = inject(AppWebBridgeService);
  private readonly currentComponentService = inject(CurrentComponentService);
  private readonly destroyRef = inject(DestroyRef);

  private readonly FACE = this.appConfig.Shared.Regimen.Class.FACE;
  private readonly HAIR = this.appConfig.Shared.Regimen.Class.HAIR;

  processedRegimens: ProcessedRegimen[] = [];
  tabData: TabData = { count: 0, types: [], selectedTab: 0 };
  loading = true;
  hasAddOnInstructions = false;
  headerTitle = '';

  /** Per-class view state — computed once during init */
  classViewStates: Partial<Record<RegimenClass, RegimenViewState>> = {};
  /** Classes eligible for tabs on the current page */
  eligibleClasses: RegimenClass[] = [];
  showTabs = false;
  showHeader = true;
  /** Ordered classes for visible tabs — used to project content into sequential slots */
  visibleTabLabels: RegimenTabClass[] = [];

  async ngOnInit(): Promise<void> {
    const rawRegimens = await this.regimenService.getRegimens();
    this.processedRegimens = rawRegimens.map(r => processRegimen(r));

    await Promise.all([this.checkAddOnInstructions(), this.buildClassViewStates()]);

    this.refreshForChildRoute();

    this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(() => this.refreshForChildRoute());

    this.route.queryParams.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(params => {
      const regimenClass = params[REGIMEN_QUERY_PARAMS.CLASS] as RegimenTabClass | undefined;
      if (regimenClass) this.updateSelectedTab(regimenClass);
    });

    this.loading = false;
    this.appBridge.setStatusBarColor(REGIMEN_STATUS_BAR_COLOR);
  }

  /** Recompute the child-route-derived view state (header + tabs). Runs on init and on every
   *  child-route navigation so the tab set always matches the currently rendered child. */
  private refreshForChildRoute(): void {
    const childPath = this.route.firstChild?.snapshot?.routeConfig?.path;
    this.showHeader = childPath !== REGIMEN_ROUTE_PATHS.PREPARATION;
    this.computeTabVisibility(childPath);
    this.setTabData();
    this.updateHeaderTitle(childPath);
  }

  tabChange(event: TabChangeEvent): void {
    if (event.type === REGIMEN_TAB_TYPES.OTHERS) {
      this.router.navigate([REGIMEN_ROUTE_PATHS.ROOT, REGIMEN_ROUTE_PATHS.INSTRUCTIONS], {
        queryParams: { [REGIMEN_QUERY_PARAMS.CLASS]: REGIMEN_TAB_TYPES.OTHERS },
        replaceUrl: true,
      });
      return;
    }
    // Map tab index → eligible class (fixed order: FACE at 0, HAIR at 1)
    const regimenClass = this.eligibleClasses[event.index];
    if (!regimenClass) return;

    let childPath = this.route.firstChild?.snapshot?.routeConfig?.path;
    // If currently on addon-instructions, switch back to instructions
    if (childPath === REGIMEN_ROUTE_PATHS.ADDON_INSTRUCTIONS) {
      childPath = REGIMEN_ROUTE_PATHS.INSTRUCTIONS;
    }
    this.router.navigate([REGIMEN_ROUTE_PATHS.ROOT, childPath], {
      queryParams: { [REGIMEN_QUERY_PARAMS.CLASS]: regimenClass },
      replaceUrl: true,
    });
  }

  back(): void {
    const handler = this.currentComponentService?.component?.handleBackPress;
    if (handler) {
      handler().catch((): void => undefined);
      return;
    }
    this.location.back();
  }

  private updateHeaderTitle(childPath: string | undefined): void {
    if (childPath === REGIMEN_ROUTE_PATHS.INSTRUCTIONS || childPath === REGIMEN_ROUTE_PATHS.ADDON_INSTRUCTIONS) {
      this.headerTitle = $localize`:@@instructions:Instructions`;
    } else {
      this.headerTitle = $localize`:@@getYourTreatment:Get your treatment kit`;
    }
  }

  /** Compute and store the view state for each regimen class */
  private async buildClassViewStates(): Promise<void> {
    for (const regimenClass of [this.FACE, this.HAIR]) {
      const regimen = this.processedRegimens.find(r => r.class === regimenClass) ?? null;
      let consultationSession: { get(key: string): unknown } | null = null;
      if (!regimen?.objectId) {
        consultationSession = await this.conn.findConsultationSession(regimenClass);
      }
      this.classViewStates[regimenClass] = getDisplayState(regimen, { consultationSession });
    }
  }

  /** Compute which tabs to show based on the current child route */
  private computeTabVisibility(childPath: string | undefined): void {
    this.eligibleClasses = this.getEligibleClasses(childPath);
    const isInstructionPage = childPath === REGIMEN_ROUTE_PATHS.INSTRUCTIONS
      || childPath === REGIMEN_ROUTE_PATHS.ADDON_INSTRUCTIONS;

    this.visibleTabLabels = [];
    if (this.eligibleClasses.includes(this.FACE)) this.visibleTabLabels.push(this.FACE);
    if (this.eligibleClasses.includes(this.HAIR)) this.visibleTabLabels.push(this.HAIR);
    if (this.hasAddOnInstructions && isInstructionPage) this.visibleTabLabels.push(REGIMEN_TAB_TYPES.OTHERS);

    this.showTabs = this.visibleTabLabels.length >= 2;
  }

  /** Get eligible classes for a given child route */
  private getEligibleClasses(childPath: string | undefined): RegimenClass[] {
    const classes = [this.FACE, this.HAIR];

    switch (childPath) {
      case REGIMEN_ROUTE_PATHS.INSTRUCTIONS:
      case REGIMEN_ROUTE_PATHS.ADDON_INSTRUCTIONS:
        return classes.filter(cls =>
          this.classViewStates[cls] === REGIMEN_VIEW_STATES.INSTRUCTION
          || this.classViewStates[cls] === REGIMEN_VIEW_STATES.UNPAID,
        );
      case REGIMEN_ROUTE_PATHS.EMPTY:
        return classes.filter(cls => this.classViewStates[cls] === REGIMEN_VIEW_STATES.EMPTY);
      case REGIMEN_ROUTE_PATHS.UNPAID:
        return classes.filter(cls => this.classViewStates[cls] === REGIMEN_VIEW_STATES.UNPAID);
      default:
        return [];
    }
  }

  private setTabData(): void {
    if (!this.showTabs) {
      this.tabData = { count: 0, types: [], selectedTab: 0 };
      return;
    }

    const types: string[] = this.visibleTabLabels.map(label =>
      label === REGIMEN_TAB_TYPES.OTHERS ? REGIMEN_TAB_TYPES.OTHERS : REGIMEN_TAB_TYPES.REGIMEN,
    );

    const queryClass = this.route.firstChild?.snapshot?.queryParams?.[REGIMEN_QUERY_PARAMS.CLASS] as RegimenTabClass | undefined;
    const childPath = this.route.firstChild?.snapshot?.routeConfig?.path;
    const selectedTab = this.computeSelectedTabIndex(queryClass, childPath);

    this.tabData = { count: types.length, types, selectedTab };
  }

  private updateSelectedTab(regimenClass: RegimenTabClass): void {
    const childPath = this.route.firstChild?.snapshot?.routeConfig?.path;
    const idx = this.computeSelectedTabIndex(regimenClass, childPath);
    this.tabData = { ...this.tabData, selectedTab: idx };
  }

  /** Resolve the active tab index based on the current class query param and child route */
  private computeSelectedTabIndex(queryClass: RegimenTabClass | undefined, childPath: string | undefined): number {
    if (queryClass === REGIMEN_TAB_TYPES.OTHERS || childPath === REGIMEN_ROUTE_PATHS.ADDON_INSTRUCTIONS) {
      return Math.max(0, this.visibleTabLabels.indexOf(REGIMEN_TAB_TYPES.OTHERS));
    }
    const regimenClass = queryClass ?? this.FACE;
    if (regimenClass !== this.FACE && regimenClass !== this.HAIR) return 0;
    return Math.max(0, this.visibleTabLabels.indexOf(regimenClass));
  }

  private async checkAddOnInstructions(): Promise<void> {
    const user = this.conn.getActingUser();
    const cachedAddOn = this.instructionsService.getAddOnInstructions();
    if (cachedAddOn?.length > 0) {
      this.hasAddOnInstructions = true;
    } else if (user?.get('orderState') === this.appConfig.Shared.User.OrderState.DELIVERED) {
      const instructions = await this.conn.getInstructionsForUser();
      if (instructions) this.instructionsService.setUserInstructions(instructions);
      this.hasAddOnInstructions = (instructions?.userInstructions?.ADDITIONAL_PRODUCTS?.length ?? 0) > 0;
    }
  }
}
